// Song questionnaire: 6 steps, local draft save, wait screen with video, preview, offer selection.

const CURRENCY = "$";
// Progress labels follow the wait video (percent thresholds)
const STAGES = [
  [0, "Reading your answers..."],
  [12, "Writing the lyrics..."],
  [30, "Composing melody..."],
  [55, "Recording vocals..."],
  [78, "Mixing the track..."],
  [95, "Adding final touches..."],
];

const STORAGE_KEY = "odearly-quiz-v1";
const API = (window.ODEARLY_API || "").replace(/\/$/, "");
const TOTAL = 6;
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// Draft helper: add ?fast to the URL to run the wait video at 16x
const FAST = new URLSearchParams(location.search).has("fast");

const form = document.querySelector("[data-quiz]");
if (navigator.webdriver) { form.innerHTML = '<p class="q-sub">Please open this page in a regular browser to create a song.</p>'; throw new Error("automation blocked"); }
const steps = [...form.querySelectorAll(".q-step")];
const nextBtn = form.querySelector("[data-next]");
const backBtn = form.querySelector("[data-back]");
const progress = document.querySelector("[data-progress]");
const progressLabel = document.querySelector("[data-progress-label]");
const progressWrap = document.querySelector("[data-progress-wrap]");
const savedNote = form.querySelector("[data-saved]");
const waitEl = document.querySelector("[data-wait]");
const offerEl = document.querySelector("[data-offer]");

let current = 1;

// ---------- storage (optional, never required to work) ----------
const store = {
  load() { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; } },
  save(data) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); return true; } catch { return false; } },
  patch(extra) { const d = this.load() || {}; return this.save({ ...d, ...extra }); },
  clear() { try { localStorage.removeItem(STORAGE_KEY); } catch {} },
};

function readAnswers() {
  const data = Object.fromEntries(new FormData(form).entries());
  data.marketing = form.elements.marketing.checked;
  return data;
}

function writeAnswers(data) {
  Object.entries(data).forEach(([key, value]) => {
    const field = form.elements[key];
    if (!field) return;
    if (field instanceof RadioNodeList) {
      [...field].forEach((r) => { r.checked = r.value === value; });
    } else if (field.type === "checkbox") {
      field.checked = !!value;
    } else {
      field.value = value;
    }
  });
}

// ---------- name tokens ----------
function paintName() {
  const name = (form.elements.name.value || "").trim();
  document.querySelectorAll("[data-name]").forEach((el) => {
    if (!el.dataset.fallback) el.dataset.fallback = el.textContent;
    el.textContent = name || el.dataset.fallback;
  });
  document.querySelectorAll("[data-name-cap]").forEach((el) => {
    if (!el.dataset.fallback) el.dataset.fallback = el.textContent;
    el.textContent = name ? `${name}'s` : el.dataset.fallback;
  });
}

// ---------- the song card that fills in while they answer ----------
const HINTS = {
  1: "Every answer becomes a line of the song.",
  2: "The style sets the mood of the first verse.",
  3: "Their character gives the song its voice.",
  4: "Memories become the verses.",
  5: "Your message becomes the chorus.",
  6: "One last step, then we start writing.",
};
function paintCard() {
  const a = readAnswers();
  const card = document.querySelector("[data-songcard]");
  if (!card) return;
  const name = (a.name || "").trim();
  card.querySelector("[data-card-name]").textContent = name || "someone you love";
  const occ = a.occasion ? `${a.relationship ? `${a.relationship} · ` : ""}${a.occasion}` : (a.relationship || "");
  card.querySelector("[data-card-occasion]").innerHTML = occ || "&nbsp;";
  const tags = [a.genre, a.mood, a.voice && a.voice !== "No preference" ? `${a.voice} voice` : null, a.language && a.language !== "English" ? a.language : null].filter(Boolean);
  card.querySelector("[data-card-tags]").innerHTML = tags.map((t) => `<li>${t}</li>`).join("");
  card.querySelector("[data-card-hint]").textContent = HINTS[current] || HINTS[1];
  card.classList.toggle("has-name", Boolean(name));
}

// ---------- counters + idea sparks ----------
form.querySelectorAll("textarea").forEach((ta) => {
  const count = form.querySelector(`[data-count-for="${ta.name}"]`);
  const update = () => { if (count) count.textContent = `${ta.value.length} / ${ta.maxLength}`; };
  ta.addEventListener("input", update);
  update();
});

form.querySelectorAll("[data-spark]").forEach((btn) => {
  btn.addEventListener("click", () => {
    const ta = form.elements[btn.dataset.spark];
    const add = btn.textContent.trim();
    const sep = ta.value && !/[\s,.]$/.test(ta.value) ? ", " : "";
    ta.value = `${ta.value}${sep}${add}${ta.name === "character" ? "" : ": "}`;
    ta.dispatchEvent(new Event("input"));
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  });
});

// ---------- validation ----------
const PHONE_RE = /^\+?[0-9][0-9\s().-]{6,22}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function isValid(wrap) {
  const key = wrap.dataset.required;
  const field = form.elements[key];
  if (field instanceof RadioNodeList) return !!field.value;
  if (key === "email") return EMAIL_RE.test(field.value.trim());
  if (key === "phone") return PHONE_RE.test(field.value.trim());
  return field.value.trim().length >= Number(wrap.dataset.min || 1);
}

function validateStep(n) {
  const step = steps[n - 1];
  let firstInvalid = null;
  step.querySelectorAll("[data-required]").forEach((wrap) => {
    const key = wrap.dataset.required;
    const field = form.elements[key];
    const error = step.querySelector(`[data-error-for="${key}"]`);
    const ok = isValid(wrap);
    wrap.classList.toggle("has-error", !ok);
    if (error) error.hidden = ok;
    if (!(field instanceof RadioNodeList)) field.setAttribute("aria-invalid", String(!ok));
    if (!ok && !firstInvalid) firstInvalid = wrap;
  });
  if (firstInvalid) {
    firstInvalid.classList.remove("shake");
    void firstInvalid.offsetWidth;
    firstInvalid.classList.add("shake");
    firstInvalid.querySelector("input, textarea, select")?.focus({ preventScroll: true });
    firstInvalid.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
  }
  return !firstInvalid;
}

// Clear an error as soon as the field becomes valid
["input", "change"].forEach((type) => form.addEventListener(type, (e) => {
  const wrap = e.target.closest("[data-required]");
  if (wrap?.classList.contains("has-error") && isValid(wrap)) {
    wrap.classList.remove("has-error");
    const error = form.querySelector(`[data-error-for="${wrap.dataset.required}"]`);
    if (error) error.hidden = true;
  }
  persist();
  paintCard();
}));

// ---------- navigation ----------
const track = (e, d) => { try { window.odTrack?.(e, d); } catch {} };
function show(n, { focus = true } = {}) {
  current = Math.min(Math.max(n, 1), TOTAL);
  track("quiz_step", { n: current });
  steps.forEach((s, i) => {
    const active = i + 1 === current;
    s.hidden = !active;
    s.classList.toggle("is-entering", active);
  });
  backBtn.hidden = current === 1;
  nextBtn.innerHTML = current === TOTAL
    ? 'Write my song <i class="ph ph-music-notes" aria-hidden="true"></i>'
    : 'Continue <i class="ph ph-arrow-right" aria-hidden="true"></i>';
  progress.style.transform = `scaleX(${((current - 1) / TOTAL) + 1 / TOTAL / 2})`;
  progressLabel.textContent = `${current} / ${TOTAL}`;
  paintName();
  paintCard();
  if (focus) {
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    steps[current - 1].querySelector(".q-title").focus({ preventScroll: true });
  }
  persist();
}

let saveTimer;
function persist() {
  if (form.hidden) return;
  const ok = store.save({ step: current, answers: readAnswers() });
  clearTimeout(saveTimer);
  if (ok) {
    savedNote.textContent = "Answers saved on this device";
    saveTimer = setTimeout(() => { savedNote.textContent = ""; }, 1800);
  }
}

form.addEventListener("submit", (e) => {
  e.preventDefault();
  if (!validateStep(current)) return;
  if (current < TOTAL) show(current + 1);
  else finish();
});
backBtn.addEventListener("click", () => show(current - 1));

// Enter in single-line inputs moves forward; textareas keep new lines
form.addEventListener("keydown", (e) => {
  if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "checkbox") {
    e.preventDefault();
    nextBtn.click();
  }
});

// ---------- brief ----------
function buildBrief(a) {
  return {
    created_at: new Date().toISOString(),
    recipient: { relationship: a.relationship, name: a.name?.trim(), pronunciation: a.pronunciation?.trim() || null },
    occasion: a.occasion,
    music: { genre: a.genre, voice: a.voice, mood: a.mood, language: a.language },
    story: { character: a.character?.trim(), memories: a.memories?.trim(), message: a.message?.trim(), extras: a.extras?.trim() || null },
    customer: { email: a.email?.trim(), phone: a.phone?.trim() || null, marketing_opt_in: !!a.marketing },
    hp: a.hp || "",
    ...(window.odCookies ? window.odCookies() : {}),
  };
}

// ---------- audio players (preview) ----------
function buildWave(el, bars = 40) {
  if (el.children.length) return;
  let seed = Number(el.dataset.seed) || 1;
  const rand = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  for (let i = 0; i < bars; i++) {
    const envelope = Math.sin((i / (bars - 1)) * Math.PI) * 0.6 + 0.4;
    const bar = document.createElement("i");
    bar.style.setProperty("--h", `${Math.max(18, Math.round(envelope * (40 + rand() * 60)))}%`);
    el.appendChild(bar);
  }
}

const players = [];
function initPlayer(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = "1";
  const btn = root.querySelector("[data-play]");
  const icon = btn.querySelector("i");
  const time = root.querySelector("[data-time]");
  const wave = root.querySelector("[data-wave]");
  buildWave(wave);
  const bars = [...wave.children];
  const audio = new Audio(root.dataset.src);
  audio.preload = "metadata";
  players.push(audio);
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  audio.addEventListener("loadedmetadata", () => { time.textContent = fmt(audio.duration); });
  audio.addEventListener("timeupdate", () => {
    if (!audio.duration) return;
    const lit = Math.round((audio.currentTime / audio.duration) * bars.length);
    bars.forEach((b, i) => b.classList.toggle("on", i < lit));
    time.textContent = fmt(audio.duration - audio.currentTime);
  });
  audio.addEventListener("play", () => { root.classList.add("is-playing"); icon.className = "ph-fill ph-pause"; });
  audio.addEventListener("pause", () => { root.classList.remove("is-playing"); icon.className = "ph-fill ph-play"; });
  audio.addEventListener("ended", () => { if (root.hasAttribute("data-preview-player")) document.dispatchEvent(new Event("preview:ended")); });
  btn.addEventListener("click", () => {
    if (audio.paused) {
      players.forEach((p) => { if (p !== audio) p.pause(); });
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  });
}

// ---------- wait screen ----------
// Draft helper: add ?reset to the URL to start the questionnaire from scratch
if (new URLSearchParams(location.search).has("reset")) store.clear();

const video = waitEl.querySelector("[data-wait-video]");
const soundBtn = waitEl.querySelector("[data-wait-sound]");
const startBtn = waitEl.querySelector("[data-wait-start]");
const stageEl = waitEl.querySelector("[data-wait-stage]");
const pctEl = waitEl.querySelector("[data-wait-pct]");
const barEl = waitEl.querySelector("[data-wait-bar]");
const progressEl = waitEl.querySelector("[data-wait-progress]");
const noteEl = waitEl.querySelector("[data-wait-note]");
const readyEl = waitEl.querySelector("[data-wait-ready]");
const FALLBACK_SECONDS = 710; // length of wait.mp4, used until the video reports its own duration
let waitFinished = false;
let clock = null;

function paintProgress(pct) {
  const p = Math.max(0, Math.min(100, Math.floor(pct)));
  pctEl.textContent = `${p}%`;
  barEl.style.transform = `scaleX(${p / 100})`;
  progressEl.setAttribute("aria-valuenow", String(p));
  stageEl.textContent = STAGES.filter(([at]) => p >= at).pop()[1];
}

function setSound(on) {
  video.muted = !on;
  soundBtn.setAttribute("aria-pressed", String(on));
  soundBtn.innerHTML = on
    ? '<i class="ph ph-speaker-high" aria-hidden="true"></i><span>Sound on</span>'
    : '<i class="ph ph-speaker-slash" aria-hidden="true"></i><span>Tap for sound</span>';
}

soundBtn.addEventListener("click", () => {
  setSound(video.muted);
  if (video.paused) video.play().catch(() => {});
});
startBtn.addEventListener("click", () => {
  startBtn.hidden = true;
  setSound(true);
  video.play().catch(() => { startBtn.hidden = false; });
});

function waitTotal() {
  return video.duration && isFinite(video.duration) ? video.duration : FALLBACK_SECONDS;
}

function elapsedSeconds() {
  const started = store.load()?.waitStartedAt || Date.now();
  return ((Date.now() - started) / 1000) * (FAST ? 16 : 1);
}

video.addEventListener("loadedmetadata", () => {
  // Resume the video where the customer would be if they had never left
  const at = elapsedSeconds();
  if (at > 1 && at < video.duration - 1) video.currentTime = at;
});
video.addEventListener("ended", () => { videoEnded = true; tick(); });

// ---------- song job (backend) ----------
let videoEnded = false;
let song = null; // last status from the server
let pollTimer = null;

async function createSong(brief) {
  try {
    const res = await fetch(`${API}/api/songs`, { method: "POST", headers: { "Content-Type": "application/json", "X-Odearly": "1" }, body: JSON.stringify(brief) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { id } = await res.json();
    store.patch({ songId: id });
    return id;
  } catch {
    store.patch({ songId: null, songError: true });
    return null;
  }
}

async function pollSong() {
  const id = store.load()?.songId;
  if (!id) return;
  try {
    const res = await fetch(`${API}/api/songs/${id}`, { cache: "no-store" });
    if (res.status === 404) { store.patch({ songId: null, songError: true }); return; }
    if (!res.ok) return;
    song = await res.json();
    if (song.status === "done") { store.patch({ preview: { url: song.preview_url, lyrics: song.lyrics, title: song.title } }); clearInterval(pollTimer); }
    if (song.status === "failed") { store.patch({ songError: true }); clearInterval(pollTimer); }
    tick();
  } catch { /* offline for a moment: keep polling */ }
}

function songReady() { return Boolean(store.load()?.preview?.url); }
function songFailed() { const s = store.load(); return Boolean(s?.songError) && !s?.preview; }

// The bar follows the video, but never reaches 100% before the song exists
function tick() {
  if (waitFinished) return;
  const total = waitTotal();
  let done = Math.max(video.currentTime / total, elapsedSeconds() / total);
  const videoDone = videoEnded || done >= 1;
  if (videoDone && (songReady() || songFailed())) { endWait(); return; }
  if (videoDone) done = 0.98; // song still rendering: hold at 98% and keep the video paused on the last frame
  paintProgress(done * 100);
  if (videoDone) stageEl.textContent = songFailed() ? "Almost there..." : "Adding final touches...";
}

function startWait() {
  track("wait_start");
  form.hidden = true;
  offerEl.hidden = true;
  progressWrap.style.visibility = "hidden";
  waitEl.hidden = false;
  paintName();
  window.scrollTo({ top: 0 });
  waitEl.querySelector(".q-wait__title").focus({ preventScroll: true });
  if (!store.load()?.waitStartedAt) store.patch({ waitStartedAt: Date.now() });
  if (FAST) video.playbackRate = 16;
  // Play WITH sound. Works right after the customer's click on "Write my song";
  // on a reload the browser may refuse sound without a tap, so we show a play button instead of going silent.
  setSound(true);
  video.play().then(() => { startBtn.hidden = true; }).catch(() => { startBtn.hidden = false; });
  tick();
  clearInterval(clock);
  clock = setInterval(tick, 500);
  clearInterval(pollTimer);
  pollSong();
  pollTimer = setInterval(pollSong, 5000);
}

function applyPreview(root) {
  const preview = store.load()?.preview;
  const pending = root.parentElement.querySelector("[data-no-preview]");
  if (preview?.url) {
    track("preview_ready", { song: store.load()?.songId || "" });
    root.dataset.src = preview.url;
    root.hidden = false;
    if (pending) pending.hidden = true;
    return true;
  }
  // No real song yet: never play someone else's song as theirs
  root.hidden = true;
  if (pending) {
    pending.hidden = false;
    const email = store.load()?.answers?.email;
    const slot = pending.querySelector("[data-customer-email]");
    if (slot && email) slot.textContent = email;
  }
  return false;
}

function endWait() {
  if (waitFinished) return;
  waitFinished = true;
  clearInterval(clock);
  clearInterval(pollTimer);
  video.pause();
  paintProgress(100);
  stageEl.textContent = songReady() ? "Your preview is ready" : "Your preview will arrive by email";
  store.patch({ waitDone: true });
  // No extra click: the decision page opens by itself with the preview at the top
  setTimeout(() => showOffer(store.load()?.brief), 900);
}

waitEl.querySelector("[data-wait-continue]").addEventListener("click", () => {
  players.forEach((p) => p.pause());
  showOffer(store.load()?.brief);
});

async function finish() {
  track("quiz_submit", { lang: form.elements.language?.value });
  const answers = readAnswers();
  const brief = buildBrief(answers);
  store.save({ step: TOTAL, answers, brief, done: true, waitDone: false, waitStartedAt: Date.now(), songId: null, preview: null, songError: false });
  startWait();
  await createSong(brief);
  pollSong();
}

// ---------- offer ----------
// Prices mirror the Shopify product "Odearly Personalized Song" (variant IDs for checkout). Edit there first, then here.
const PLANS = [
  { id: "3h", variant: 63540830273866, title: "3-Hour Priority", desc: "Front of the studio queue. Full song in your inbox within 3 hours.", price: 128.99, compare: 228.99, tag: "Fastest · biggest saving", note: "Only $30 more than 6-Hour. In their hands before dinner." },
  { id: "6h", variant: 63552638615882, title: "6-Hour Express", desc: "Same song, same quality, delivered within 6 hours. The option most people choose.", price: 98.99, compare: 178.99, badge: "Most popular", note: "Only $20 more than 24-Hour, ready tonight" },
  { id: "24h", variant: 63540830208330, title: "24-Hour Rush", desc: "Delivered within 24 hours, same studio quality.", price: 78.99, compare: 138.99 },
];
const ADDONS = [
  { id: "lyrics", variant: 63552948502858, title: "Printable lyrics", desc: "A designed lyric sheet with their name and cover art. Print it, frame it, keep it.", price: 19.99, badge: "Most added" },
  { id: "spotify", variant: 63552948830538, title: "Release on Spotify", desc: "Their song on Spotify, Apple Music and more. Share it with the world.", price: 49.99, compare: 99.99, badge: "Fan favourite" },
  { id: "revision", variant: 63540830929226, title: "Extra revision", desc: "One more revision pass after delivery, for bigger lyric or style changes.", price: 19.99 },
  { id: "license", variant: 63540831060298, title: "Commercial use license", desc: "Use the song in business, advertising or monetized content.", price: 49.99 },
];
const FEATURED = PLANS.find((p) => p.badge) || PLANS[0];
const BUNDLE_TIERS = { 2: 20, 4: 40 };  // mirrors the Shopify automatic discounts (2+ extras: $20 off, all 4: $40 off)
const BUNDLE_DISCOUNT = BUNDLE_TIERS[2];
const COMEBACK = { code: "COMEBACK10", amount: 10 };   // Shopify discount code shown to people who press back on this page
const comebackOn = () => !!store.load()?.comeback;
function bundleFor(n) { return n >= 4 ? BUNDLE_TIERS[4] : n >= 2 ? BUNDLE_TIERS[2] : 0; }
const TIMER_MINUTES = 20;
function money(n) { n = Math.round(n * 100) / 100; return `${CURRENCY}${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)}`; }

let offersRendered = false;
function renderOffers() {
  if (offersRendered) return;
  offersRendered = true;
  offerEl.querySelector("[data-plans]").innerHTML = PLANS.map((p, i) => `
    <label class="plan${p.badge || p.tag ? " plan--featured" : ""}${p.tag ? " plan--fast" : ""}" data-plan-card="${p.id}">
      <input type="radio" name="plan" value="${p.id}" ${p === FEATURED ? "checked" : ""}>
      ${p.badge ? `<span class="plan__badge">${p.badge}</span>` : ""}${p.tag ? `<span class="plan__badge plan__badge--tag">${p.tag}</span>` : ""}
      <span class="plan__body">
        <span class="plan__title">${p.title}</span>
        <span class="plan__desc">${p.desc}</span>
        ${p.note ? `<span class="plan__note">${p.note}</span>` : ""}
      </span>
      <span class="plan__price">${p.compare ? `<s>${money(p.compare)}</s>` : ""}<strong>${money(p.price)}</strong>${p.compare ? `<em>Save ${money(p.compare - p.price)}</em>` : ""}</span>
    </label>`).join("") + `<p class="upsell" data-upsell hidden></p>`;
  offerEl.querySelector("[data-plans]").addEventListener("click", (e) => {
    const btn = e.target.closest("[data-upsell-to]");
    if (!btn) return;
    const input = offerEl.querySelector(`input[name="plan"][value="${btn.dataset.upsellTo}"]`);
    if (!input) return;
    input.checked = true;
    updateTotal();
    const card = input.closest(".plan");
    pop(card, "is-chosen");
    card.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
  });
  offerEl.querySelector("[data-addons]").innerHTML = ADDONS.map((a) => `
    <label class="addon">
      <input type="checkbox" name="addon" value="${a.id}">
      <span class="addon__body">
        <span class="addon__title">${a.title}${a.badge ? `<span class="addon__badge">${a.badge}</span>` : ""}</span>
        <span class="addon__desc">${a.desc}</span>
      </span>
      <span class="addon__price">${a.compare ? `<s>${money(a.compare)}</s>` : ""}<strong>+${money(a.price)}</strong></span>
    </label>`).join("") + (BUNDLE_DISCOUNT ? `
    <div class="bundle" data-bundle data-state="0">
      <div class="bundle__track" aria-hidden="true"><i class="bundle__seg"></i><i class="bundle__seg"></i><i class="bundle__seg"></i><i class="bundle__seg"></i></div>
      <div class="bundle__row">
        <span class="bundle__icon" aria-hidden="true"><i class="ph-fill ph-gift"></i></span>
        <p class="bundle__text" data-bundle-hint aria-live="polite">Pick any 2 extras and save ${money(BUNDLE_TIERS[2])}. All 4: ${money(BUNDLE_TIERS[4])} off.</p>
        <span class="bundle__chip" data-bundle-chip>up to ${money(BUNDLE_TIERS[4])} off</span>
      </div>
    </div>` : "");
  // The preview-unlocked rate follows the featured plan
  const off = FEATURED.compare - FEATURED.price;
  offerEl.querySelector("[data-rate-off]").textContent = `${money(off)} off`;
  offerEl.querySelector("[data-rate-pct]").textContent = String(Math.round((off / FEATURED.compare) * 100));
  offerEl.addEventListener("change", (e) => { updateTotal(); if (e.target.name === "plan") track("plan_select", { plan: e.target.value }); if (e.target.name === "addon") track(e.target.checked ? "addon_on" : "addon_off", { addon: e.target.value }); });
  offerEl.querySelectorAll("[data-scroll-offer]").forEach((b) => b.addEventListener("click", () => {
    document.getElementById("unlock").scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  }));
}

function selection() {
  const plan = PLANS.find((p) => p.id === offerEl.querySelector('input[name="plan"]:checked')?.value) || FEATURED;
  const addons = [...offerEl.querySelectorAll('input[name="addon"]:checked')].map((i) => ADDONS.find((a) => a.id === i.value));
  const bundle = bundleFor(addons.length);
  const comeback = comebackOn() ? COMEBACK.amount : 0;
  const total = plan.price + addons.reduce((s, a) => s + a.price, 0) - bundle - comeback;
  const compare = (plan.compare || plan.price) + addons.reduce((s, a) => s + (a.compare || a.price), 0);
  return { plan, addons, bundle, comeback, total, compare };
}

// Numbers glide to their new value instead of jumping
function tween(el, to, fmt = money) {
  const from = Number(el.dataset.value ?? to);
  el.dataset.value = String(to);
  if (reduceMotion || from === to || !el.isConnected) { el.textContent = fmt(to); return; }
  const start = performance.now(), dur = 450, id = (el._tween = (el._tween || 0) + 1);
  const step = (now) => {
    if (el._tween !== id) return;
    const t = Math.min(1, (now - start) / dur), e = 1 - Math.pow(1 - t, 4);
    el.textContent = fmt(from + (to - from) * e);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
  setTimeout(() => { if (el._tween === id) el.textContent = fmt(to); }, dur + 80); // background tabs pause rAF
}
function pop(el, cls = "is-pop") {
  if (!el || reduceMotion) return;
  el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls);
}
let lastBundle = 0;
function updateTotal() {
  const { plan, addons, bundle, comeback, total, compare } = selection();
  const cb = offerEl.querySelector("[data-comeback-line]");
  if (cb) cb.hidden = !comeback;
  const totalEl = offerEl.querySelector("[data-total]");
  tween(totalEl, total); pop(totalEl);
  const cmp = offerEl.querySelector("[data-compare]");
  cmp.textContent = compare > total ? money(compare) : "";
  const save = offerEl.querySelector("[data-save]");
  if (save) { const amount = Math.max(0, compare - total); save.hidden = amount <= 0; tween(save.querySelector("b"), amount); if (bundle !== lastBundle) pop(save); }
  offerEl.querySelector("[data-plan-label]").textContent = plan.title + (addons.length ? ` + ${addons.length} extra${addons.length > 1 ? "s" : ""}` : "");

  // Bundle meter: 0-1 = working toward $20, 2-3 = $20 unlocked, 4 = complete set $40
  const box = offerEl.querySelector("[data-bundle]");
  if (box) {
    const n = addons.length;
    box.dataset.state = String(Math.min(4, n));
    box.dataset.tier = n >= 4 ? "2" : n >= 2 ? "1" : "0";
    box.querySelector("[data-bundle-hint]").textContent =
      n >= 4 ? `Complete set: ${money(BUNDLE_TIERS[4])} off, applied automatically at checkout.`
      : n === 3 ? `${money(BUNDLE_TIERS[2])} off unlocked. Add the last extra and it becomes ${money(BUNDLE_TIERS[4])} off.`
      : n === 2 ? `Bundle unlocked: ${money(BUNDLE_TIERS[2])} off. Complete the set for ${money(BUNDLE_TIERS[4])} off.`
      : n === 1 ? `One more extra unlocks ${money(BUNDLE_TIERS[2])} off.`
      : `Pick any 2 extras and save ${money(BUNDLE_TIERS[2])}. All 4: ${money(BUNDLE_TIERS[4])} off.`;
    box.querySelector("[data-bundle-chip]").innerHTML = bundle ? `<i class="ph-bold ph-check" aria-hidden="true"></i> ${money(bundle)} off` : `up to ${money(BUNDLE_TIERS[4])} off`;
    if (bundle > lastBundle) { pop(box, "is-unlocked-anim"); pop(offerEl.querySelector(".buybar__price"), "is-glow"); }
    offerEl.querySelectorAll(".addon").forEach((card) => card.classList.toggle("is-bundled", !!bundle && card.querySelector("input").checked));
  }

  // Speed upsell: one tap to the next faster plan
  const up = offerEl.querySelector("[data-upsell]");
  if (up) {
    const faster = PLANS.filter((p) => p.price > plan.price).sort((a, b) => a.price - b.price)[0];
    if (faster) {
      up.hidden = false;
      up.innerHTML = `<i class="ph-fill ph-lightning" aria-hidden="true"></i><span>Make it <strong>${faster.title.replace(/ (Priority|Express|Rush)$/, "")}</strong> for <strong>+${money(faster.price - plan.price)}</strong></span><button type="button" class="upsell__btn" data-upsell-to="${faster.id}">Upgrade</button>`;
    } else {
      up.hidden = false;
      up.innerHTML = `<i class="ph-fill ph-rocket-launch" aria-hidden="true"></i><span>Fastest option locked in: <strong>in their inbox within 3 hours</strong>.</span>`;
    }
  }
  lastBundle = bundle;

  const brief = store.load()?.brief;
  if (brief) {
    brief.order = { plan: plan.id, addons: addons.map((a) => a.id), bundle_discount: bundle, total, currency: "USD", song_id: store.load()?.songId || null };
    const dbg = offerEl.querySelector("[data-brief]");
    if (dbg) dbg.textContent = JSON.stringify(brief, null, 2);
  }
}

// 20-minute launch-price countdown: one per song, kept across reloads
function startTimer() {
  const el = offerEl.querySelector("[data-timer]");
  const label = offerEl.querySelector("[data-timer-label]");
  const mini = offerEl.querySelector("[data-timer-mini]");
  const urgency = offerEl.querySelector("[data-urgency]");
  // The expiry lives with the song draft, so a new song gets a fresh 20 minutes
  let expiry = Number(store.load()?.offerExpiry) || 0;
  if (!expiry) { expiry = Date.now() + TIMER_MINUTES * 60 * 1000; store.patch({ offerExpiry: expiry }); }
  let timer = null;
  const tick = () => {
    const left = Math.max(0, expiry - Date.now());
    const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
    const text = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    el.textContent = text;
    if (mini) mini.textContent = text;
    const over = left === 0;
    urgency.classList.toggle("is-over", over);
    if (label) label.textContent = over ? "Launch price ended" : "Launch price ends in";
    clearTimeout(timer);
    if (!over) timer = setTimeout(tick, 1000 - (Date.now() % 1000));
  };
  // Background tabs throttle timers: repaint as soon as the customer comes back
  document.addEventListener("visibilitychange", () => { if (!document.hidden) tick(); });
  tick();
}

function showLyrics() {
  const lyrics = store.load()?.preview?.lyrics;
  const box = offerEl.querySelector("[data-lyrics-box]");
  if (!lyrics) { box.hidden = true; return; }
  const blocks = lyrics.split(/\n\s*\n/);
  const openCount = Math.min(2, blocks.length - 1);
  offerEl.querySelector("[data-lyrics-open]").textContent = blocks.slice(0, openCount).join("\n\n");
  offerEl.querySelector("[data-lyrics-locked]").textContent = blocks.slice(openCount).join("\n\n");
  box.hidden = false;
}

function showOffer(brief) {
  setTimeout(armComeback, 1500);
  track("offer_view", { preview: !!store.load()?.preview?.url, song: store.load()?.songId || "" });
  form.hidden = true;
  waitEl.hidden = true;
  progressWrap.style.visibility = "hidden";
  offerEl.hidden = false;
  document.body.classList.add("has-buybar");
  renderOffers();
  updateTotal();
  startTimer();
  showLyrics();
  paintSavedUntil();
  initClips();
  const offerPlayer = offerEl.querySelector("[data-preview-player]");
  if (applyPreview(offerPlayer)) initPlayer(offerPlayer);
  paintName();
  offerEl.querySelector(".q-title").focus({ preventScroll: true });
  window.scrollTo({ top: 0 });
}

// "Your song is saved until ..." = 24 h after the questionnaire was sent
function paintSavedUntil() {
  const el = offerEl.querySelector("[data-saved-until]");
  const started = store.load()?.waitStartedAt || Date.now();
  const until = new Date(started + 24 * 3600 * 1000);
  const time = until.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const sameDay = until.toDateString() === new Date().toDateString();
  if (el) el.textContent = `${sameDay ? "today" : "tomorrow"} at ${time}`;
}

// Reaction clips on the offer page: click to play with sound, one at a time
let clipsReady = false;
function initClips() {
  if (clipsReady) return;
  clipsReady = true;
  const videos = [...offerEl.querySelectorAll("[data-video] video")];
  offerEl.querySelectorAll("[data-video]").forEach((root) => {
    const video = root.querySelector("video");
    const btn = root.querySelector("[data-video-play]");
    video.addEventListener("play", () => { players.forEach((p) => p.pause()); videos.forEach((v) => { if (v !== video) v.pause(); }); root.classList.add("is-playing"); });
    video.addEventListener("pause", () => root.classList.remove("is-playing"));
    video.addEventListener("ended", () => { root.classList.remove("is-playing"); video.controls = false; });
    btn.addEventListener("click", () => { video.controls = true; video.play().catch(() => {}); });
  });
}

// When the preview finishes, show the nudge (initPlayer registers the audio in `players`)
document.addEventListener("preview:ended", () => {
  track("preview_end");
  const nudge = offerEl.querySelector("[data-nudge]");
  if (!nudge || !nudge.hidden) return;
  nudge.hidden = false;
  nudge.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
});

// ---------- comeback: pressing "back" on the decision page opens a $10 offer (real Shopify code) ----------
function armComeback() {
  if (comebackOn() || store.load()?.comebackSeen || !history.pushState) return;
  history.pushState({ odearlyOffer: true }, "");
  const onPop = () => {
    window.removeEventListener("popstate", onPop);
    if (store.load()?.comebackSeen) return;
    store.patch({ comebackSeen: true });
    const name = store.load()?.answers?.name?.trim() || "their";
    const box = document.createElement("div");
    box.className = "comeback";
    box.innerHTML = `<div class="comeback__card" role="dialog" aria-modal="true" aria-labelledby="cb-title">
      <p class="comeback__tag"><i class="ph-fill ph-gift" aria-hidden="true"></i> Before you go</p>
      <h2 class="comeback__title" id="cb-title">${money(COMEBACK.amount)} off ${name === "their" ? "their" : name + "'s"} song, today.</h2>
      <p class="comeback__sub">The first minute is written. The rest is one tap away, and we just took ${money(COMEBACK.amount)} off any delivery option.</p>
      <button class="btn btn--primary btn--lg comeback__yes" type="button">Claim ${money(COMEBACK.amount)} off <i class="ph ph-arrow-right" aria-hidden="true"></i></button>
      <button class="comeback__no" type="button">No thanks</button>
    </div>`;
    document.body.appendChild(box);
    requestAnimationFrame(() => box.classList.add("is-on"));
    track("comeback_view");
    const close = () => { box.classList.remove("is-on"); setTimeout(() => box.remove(), 350); };
    box.querySelector(".comeback__yes").addEventListener("click", () => {
      store.patch({ comeback: true }); updateTotal(); close(); track("comeback_claim");
      document.getElementById("unlock").scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    });
    box.querySelector(".comeback__no").addEventListener("click", () => { close(); history.back(); });
  };
  window.addEventListener("popstate", onPop);
}

// ---------- checkout: Shopify cart with the chosen variants ----------
const ON_SHOPIFY = typeof window.Shopify === "object" && !!window.Shopify;
const checkoutBtn = document.querySelector("[data-checkout]");
const checkoutNote = document.querySelector("[data-checkout-note]");
function noteCheckout(text) { checkoutNote.textContent = text; checkoutNote.hidden = false; }
async function checkout() {
  const { plan, addons } = selection();
  const s = store.load() || {};
  const a = s.answers || {};
  const who = [a.name?.trim(), a.relationship].filter(Boolean).join(" · ");
  const props = { "For": who || "someone you love", "Occasion": a.occasion || "", "_song_id": s.songId || "", "_customer_email": a.email || "" };
  if (!ON_SHOPIFY) { noteCheckout("Checkout isn't connected in this draft."); return; }
  const btn = checkoutBtn, label = btn.innerHTML;
  btn.disabled = true; btn.innerHTML = "<span>Preparing checkout...</span>"; checkoutNote.hidden = true;
  try {
    await fetch("/cart/clear.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: "{}" });
    const items = [plan, ...addons].filter((x) => x.variant).map((x) => ({ id: x.variant, quantity: 1, properties: props }));
    const res = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ items }) });
    if (!res.ok) throw new Error(`cart ${res.status}`);
    store.patch({ checkoutAt: Date.now(), order: { plan: plan.id, addons: addons.map((x) => x.id) } });
    track("checkout_go", { plan: plan.id, addons: addons.length, total: selection().total, song: s.songId || "", email: a.email || "" });
    const qs = [a.email ? `checkout[email]=${encodeURIComponent(a.email.trim())}` : "", comebackOn() ? `discount=${COMEBACK.code}` : ""].filter(Boolean).join("&");
    location.href = qs ? `/checkout?${qs}` : "/checkout";
  } catch {
    btn.disabled = false; btn.innerHTML = label;
    track("checkout_error");
    noteCheckout("Something went wrong on our side. Try again, or write to odearly@odearly.com and we'll finish it by hand.");
  }
}
checkoutBtn.addEventListener("click", checkout);

// Top bar slides away while the customer scrolls down and comes back on the first scroll up
(() => {
  const bar = document.querySelector(".q-top");
  if (!bar) return;
  let last = window.scrollY;
  window.addEventListener("scroll", () => {
    const y = window.scrollY;
    if (y > 120 && y > last + 4) bar.classList.add("is-hidden");
    else if (y < last - 4 || y <= 80) bar.classList.remove("is-hidden");
    last = y;
  }, { passive: true });
})();

// ---------- boot: resume where the customer left ----------
const DRAFT_TTL = 24 * 3600 * 1000; // matches "your song is saved until tomorrow"
let saved = store.load();
if (saved?.done && saved.waitStartedAt && Date.now() - saved.waitStartedAt > DRAFT_TTL) { store.clear(); saved = null; }
if (saved?.answers) writeAnswers(saved.answers);
// Arriving from a home-page chip (?for=Wife) or an ad (?for=her/him): pre-select the relationship
{
  const FOR = { her: "Wife", him: "Husband" };
  const raw = new URLSearchParams(location.search).get("for") || "";
  const rel = FOR[raw.toLowerCase()] || raw;
  const opt = rel && form.querySelector(`input[name="relationship"][value="${rel.replace(/"/g, "")}"]`);
  if (opt && !(saved?.done)) { opt.checked = true; if (!saved?.step || saved.step === 1) setTimeout(() => form.elements.name?.focus({ preventScroll: true }), 400); }
}
form.querySelectorAll("textarea").forEach((ta) => ta.dispatchEvent(new Event("input")));
if (saved?.done && saved.brief) {
  if (saved.waitDone) showOffer(saved.brief);
  else startWait();
} else {
  show(saved?.step || 1, { focus: false });
}
